import { Button, Table, Text, useModal } from "@fluidattacks/design";
import type { ColumnDef, Row } from "@tanstack/react-table";
import _ from "lodash";
import { useCallback, useMemo, useState } from "react";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { useTheme } from "styled-components";

import { useFilesQueries } from "./hooks";
import type { IFile, IFilesProps } from "./types";

import { Can } from "context/authz/can";
import { useTable } from "hooks/use-table";
import { AddFilesModal } from "pages/group/scope/files/add-files-modal";
import type { IAddFileValues } from "pages/group/scope/files/add-files-modal/types";
import { FileOptionsModal } from "pages/group/scope/files/file-options-modal";
import { formatDate } from "utils/date";

const Files: React.FC<IFilesProps> = ({
  groupName,
  organizationName,
}): JSX.Element => {
  const { t } = useTranslation();
  const theme = useTheme();
  const modal = useModal("file-modal");
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [values, setValues] = useState<IAddFileValues>({
    description: "",
    file: undefined as unknown as FileList,
  });
  const [currentRow, setCurrentRow] = useState<IFile>({
    description: "",
    fileName: "",
    id: "",
  });

  const openAddModal = useCallback((): void => {
    setIsAddModalOpen(true);
  }, []);
  const closeOptionsModal = useCallback((): void => {
    modal.close();
  }, [modal]);
  const closeAddModal = useCallback((): void => {
    setIsAddModalOpen(false);
    setValues({
      description: "",
      file: undefined as unknown as FileList,
    });
  }, []);

  const handleRowClick = useCallback(
    (rowInfo: Row<IFile>): void => {
      setCurrentRow(rowInfo.original);
      modal.open();
    },
    [modal],
  );

  const {
    apkEnvironments,
    data,
    downloadFile,
    filesDataset,
    handleRemoveFile,
    handleUpload,
    isAddingFileInfoToDB,
    isUploading,
    progressPercentage,
  } = useFilesQueries(
    closeAddModal,
    closeOptionsModal,
    currentRow.fileName,
    groupName,
    organizationName,
    setValues,
  );

  const tableHeaders: ColumnDef<IFile>[] = [
    {
      accessorKey: "fileName",
      header: t("searchFindings.filesTable.file"),
      size: 450,
    },
    {
      accessorKey: "description",
      header: t("searchFindings.filesTable.description"),
      size: 450,
    },
    {
      accessorKey: "uploadDate",
      cell: (cell): string => formatDate(String(cell.getValue())),
      header: t("searchFindings.filesTable.uploadDate"),
    },
  ];

  const formattedData = useMemo(
    (): IFile[] =>
      filesDataset.filter(({ fileName }): boolean =>
        fileName.toLowerCase().includes(search.toLowerCase()),
      ),
    [filesDataset, search],
  );

  const { table } = useTable({
    columns: tableHeaders,
    data: formattedData,
    id: "scope-files-table",
    options: {
      actionsOptions: { onSearch: setSearch, search },
      filtersMode: "client-side",
      paginationMode: "client-side",
      rowOptions: { onClick: handleRowClick },
    },
    total: formattedData.length,
  });

  if (_.isUndefined(data) || _.isEmpty(data)) {
    return <div />;
  }

  return (
    <React.Fragment>
      <Table table={table}>
        <Table.Options>
          <Table.Controls>
            <div id={"actions-container"}>
              <Can do={"integrates_api_mutations_sign_post_url_mutate"}>
                <Button
                  icon={"plus"}
                  id={"file-add"}
                  onClick={openAddModal}
                  tooltip={t("searchFindings.tabResources.files.btnTooltip")}
                  variant={"primary"}
                >
                  {t("searchFindings.tabResources.addRepository")}
                </Button>
              </Can>
            </div>
          </Table.Controls>
        </Table.Options>
        <Table.Content />
        <Table.Footer />
      </Table>
      <Text
        color={theme.palette.gray[800]}
        fontWeight={"bold"}
        mt={1.25}
        size={"md"}
      >
        {t("searchFindings.tabResources.totalFiles")}&nbsp;
        {filesDataset.length}
      </Text>
      <AddFilesModal
        isAddingFileToDB={isAddingFileInfoToDB}
        isOpen={isAddModalOpen}
        isUploading={isUploading}
        onClose={closeAddModal}
        onSubmit={handleUpload}
        progressPercentage={progressPercentage}
        values={values}
      />
      <Can
        do={"integrates_api_mutations_remove_files_mutate"}
        passThrough={true}
      >
        {(canRemove: boolean): JSX.Element => (
          <FileOptionsModal
            apkEnvironments={apkEnvironments}
            canRemove={canRemove}
            fileName={currentRow.fileName}
            filesDataSet={filesDataset}
            groupName={groupName}
            modalRef={modal}
            onClose={closeOptionsModal}
            onDelete={handleRemoveFile}
            onDownload={downloadFile}
            organizationName={organizationName}
          />
        )}
      </Can>
    </React.Fragment>
  );
};

export type { IFilesProps };
export { Files };
